// Money an operator types (#519).
//
// Every amount is stored and sent as an integer count of its currency's MINOR
// unit — USD in cents, every other currency in whole units — but an operator
// types it in the currency itself: "1.50" for a dollar fifty, "5000" for five
// thousand rupiah. These helpers set up an input for a currency and read it
// back into minor units. The decimals come from `@apx/money`, never from the
// currency registry API, which a tenant admin cannot read.

import { currencyDecimals, parseMoney } from '@apx/money';

/**
 * The `step` (and smallest `min`) of a number input that takes an amount in
 * `currency`: "0.01" for USD, "1" for a currency counted in whole units.
 */
export function moneyStep(currency: string | null | undefined): string {
  const d = currencyDecimals(currency);
  return d === 0 ? '1' : `0.${'0'.repeat(d - 1)}1`;
}

/** What an amount input accepts, for its label: "USD, up to 2 decimals",
 *  "IDR, whole units". */
export function moneyUnitHint(currency: string): string {
  const d = currencyDecimals(currency);
  return d === 0 ? `${currency}, whole units` : `${currency}, up to ${d} decimals`;
}

/** Points `input` at `currency`: its step and minimum become the currency's
 *  smallest amount, so the browser's spinner moves by one cent in USD. */
export function setMoneyInputCurrency(input: HTMLInputElement, currency: string): void {
  const step = moneyStep(currency);
  input.step = step;
  input.min = step;
}

export type MoneyRead = { ok: true; minor: bigint } | { ok: false; error: string };

/**
 * Reads an amount typed in `currency` into minor units.
 *
 * Refuses anything that is not a plain non-negative amount, more decimals than
 * the currency has ("1.505" USD, "10.5" IDR) — rounding would change what the
 * operator asked for — and, unless `allowZero`, zero.
 */
export function readMoney(
  text: string,
  currency: string,
  opts: { allowZero?: boolean } = {},
): MoneyRead {
  const minor = parseMoney(text, currency);
  if (minor === null) {
    return { ok: false, error: `Enter an amount in ${moneyUnitHint(currency)}.` };
  }
  if (minor === 0n && !opts.allowZero) {
    return { ok: false, error: 'The amount must be more than zero.' };
  }
  return { ok: true, minor };
}
